// Main-thread façade over a POOL of scan Web Workers. Owns the workers for the
// whole session, runs the capability probe ONCE (memoised), and turns a File into
// { faces, people } detections carrying their crop JPEG blobs.
//
// Throughput design (see photohawk-serverless/docs/on-device-scan-parity.md §5):
//   • A pool of N workers (scanConfig.poolSize) runs inference in parallel, so a
//     fast upload queue doesn't outrun scanning.
//   • Decode (createImageBitmap) happens INSIDE the worker — the main thread only
//     transfers the File — so decoding never janks the admin UI.
//   • EP (WebGPU/WASM) + WASM thread count are decided once (scanConfig) and passed
//     to every worker's init so the pool is consistent.
//
// Everything here is best-effort: callers treat any throw / a not-ok probe as
// "scan unavailable" and fall back to a plain upload (the backend then scans
// server-side, exactly as before). The feature can never block or fail an upload.

import { probeScanSupport } from './capability';
import { registerUploadProbe } from '../diag/uploadDiag';
import { webGpuEnabled, scanPoolSize, wasmThreadCount, ocrWebGpuEnabled, poolSize } from './scanConfig';
import { textScanEnabled, TEXT_DET_MODEL_URL, TEXT_REC_MODEL_URL, TEXT_DICT_URL } from './ppocrConfig';
import { recordScanMetrics, noteScanConfig, hostPressure, startScanSampling, stopScanSampling, scanDebugEnabled } from './scanMetrics';

const MODELS = () => {
  // Served same-origin from the admin app's public/ folder (public/models/*.onnx).
  // The assets/previews CDN doesn't send CORS headers, so a cross-origin model
  // fetch fails; hosting them under the app origin sidesteps CORS entirely.
  const base = new URL(`${import.meta.env.BASE_URL}models/`, window.location.origin).href;
  // The text config URLs are site-absolute (/models/x) but the models live in the SAME
  // dir as the YOLO ones, so resolve by BASENAME onto `base` — joining the full /models/x
  // onto base (which already ends in models/) would double it to /models/models/x.
  const abs = (p) => `${base}${p.replace(/^.*\//, '')}`;
  return {
    faceModelUrl: `${base}yolov8n-face.onnx`,
    personModelUrl: `${base}yolov8n.onnx`,
    // Text (PaddleOCR) models — only handed to the worker when the flag is on (below).
    textDetModelUrl: abs(TEXT_DET_MODEL_URL),
    textRecModelUrl: abs(TEXT_REC_MODEL_URL),
    textDictUrl: abs(TEXT_DICT_URL),
  };
};

let poolPromise = null;
let probeResult = null;
let reqId = 0;
const pending = new Map();
// One photo's on-device scan normally takes well under a few seconds (slow WASM devices ~10s). Past this
// the worker is treated as hung for that request (see scanFile).
const SCAN_TIMEOUT_MS = 60_000;
// Raw Worker handles, tracked so shutdownScanPool() can terminate every worker —
// freeing its onnxruntime WASM heap (tens of MB) + any bitmap it's mid-decode on —
// the moment the upload queue goes idle or is cancelled.
const spawnedWorkers = new Set();

// Diagnostics (gated): scan is bounded by the pool + governor, so `pending` should stay small; each
// live worker also pins its onnxruntime WASM heap (tens of MB). Reports both so a scan-side wedge
// (or a pool that didn't tear down on cancel) is visible. Byte sizes aren't known here, so items only.
registerUploadProbe('scan', () => ({
  items: pending.size,
  note: `workers=${spawnedWorkers.size}`,
}));

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

// ---- Adaptive concurrency governor ---------------------------------------
// Bounds how many scans overlap based on HOST PRESSURE (event-loop lag / long-task
// load, from scanMetrics). On a healthy machine pressure stays low and we admit the
// full width — no throttling; when the main thread starts choking we admit fewer so
// the tab stays responsive, then ramp back up as it recovers. AIMD with a hysteresis
// band so it doesn't thrash. PARITY-NEUTRAL: this only changes HOW MANY scans run at
// once, never the detection itself (each scan is byte-identical either way). Best-
// effort — if anything here throws, callers still fall back to a server scan.
// Kill switch: localStorage ph_scan_throttle = '0' (full concurrency, old behaviour).
const gov = { active: 0, target: 0, waiters: [], lastAdjust: 0 };
const PRESSURE_HIGH = 0.66; // sustained above → shed a slot
const PRESSURE_LOW = 0.33; // below → reclaim a slot (band between = hold)
const ADJUST_EVERY_MS = 500; // re-evaluate at most ~2×/s (matches the lag sampler)

function throttleEnabled() {
  try { return !(typeof localStorage !== 'undefined' && localStorage.getItem('ph_scan_throttle') === '0'); }
  catch { return true; }
}

// Full width when healthy = pool size + 2 (matches the upload-scan coroutine count),
// clamped to [1, 8]. Recomputed each adjust so a late pool-size resolution is picked up.
function govCeiling() {
  let base = 4;
  try { base = poolSize(); } catch { /* keep default */ }
  return Math.max(1, Math.min(8, (Number.isFinite(base) ? base : 4) + 2));
}

function govAdjust() {
  const max = govCeiling();
  if (!gov.target) gov.target = max; // start wide open (no throttle until pressure builds)
  const t = now();
  if (t - gov.lastAdjust < ADJUST_EVERY_MS) return;
  gov.lastAdjust = t;
  let p = 0;
  try { p = hostPressure() || 0; } catch { p = 0; }
  const before = gov.target;
  if (p > PRESSURE_HIGH && gov.target > 1) gov.target -= 1;
  else if (p < PRESSURE_LOW && gov.target < max) gov.target += 1;
  if (gov.target > max) gov.target = max; // ceiling shrank (e.g. pool rebuilt smaller)
  if (gov.target !== before) {
    try {
      if (scanDebugEnabled()) {
        // eslint-disable-next-line no-console
        console.info(`[scan-throttle] pressure=${p.toFixed(2)} → concurrency ${before}→${gov.target} (max ${max})`);
      }
    } catch { /* logging must never throw */ }
  }
}

// Acquire one admission slot; returns a single-use release fn. When throttling is
// disabled it's a no-op pass-through (full concurrency).
function govAcquire() {
  if (!throttleEnabled()) return Promise.resolve(() => {});
  govAdjust();
  const makeRelease = () => {
    let done = false;
    return () => { if (done) return; done = true; govRelease(); };
  };
  if (gov.active < gov.target) {
    gov.active += 1;
    return Promise.resolve(makeRelease());
  }
  return new Promise((resolve) => { gov.waiters.push(() => resolve(makeRelease())); });
}

function govRelease() {
  gov.active = Math.max(0, gov.active - 1);
  govAdjust();
  // Wake queued acquirers up to the current target. Each wake reserves its slot here
  // (active++) so a burst of releases can't admit past the target.
  while (gov.waiters.length && gov.active < gov.target) {
    gov.active += 1;
    const wake = gov.waiters.shift();
    try { wake(); } catch { gov.active = Math.max(0, gov.active - 1); }
  }
}

/** Live governor state for the staging dev panel: how many scans are running, the
 *  current admission ceiling, and whether throttling is enabled. Read-only. */
export function getScanGovernorState() {
  return { active: gov.active, target: gov.target, max: govCeiling(), enabled: throttleEnabled() };
}

// Reset the governor when the pool is torn down so a new batch starts wide open.
function govReset() {
  gov.active = 0;
  gov.target = 0;
  gov.lastAdjust = 0;
  const stuck = gov.waiters.splice(0);
  for (const wake of stuck) { try { wake(); } catch { /* ignore */ } }
}

// Spawn one worker and resolve its { worker, inflight } handle once it has loaded
// the models. The worker's onmessage owns the shared `pending` map + its own
// inflight counter (closed over here), so any worker can resolve any request.
function spawnWorker(initMsg) {
  return new Promise((resolve, reject) => {
    const entry = { worker: null, inflight: 0 };
    let ready = false;
    let worker;
    try {
      worker = new Worker(new URL('./scanWorker.js', import.meta.url), { type: 'module' });
    } catch (e) {
      reject(e);
      return;
    }
    spawnedWorkers.add(worker);
    entry.worker = worker;
    worker.onmessage = (e) => {
      const m = e.data;
      if (m.type === 'ready') { ready = true; resolve(entry); return; }
      if (m.type === 'error' && (m.id === undefined || m.id === null)) {
        if (!ready) reject(new Error(m.error)); // init-time failure (bad model url, ORT load error)
        return;
      }
      const p = pending.get(m.id);
      if (!p) return;
      pending.delete(m.id);
      entry.inflight = Math.max(0, entry.inflight - 1);
      if (m.type === 'error') p.reject(new Error(m.error));
      else p.resolve(m);
    };
    worker.onerror = (err) => {
      if (!ready) { reject(err instanceof Error ? err : new Error('scan worker failed')); return; }
      // A crash after init (e.g. a WASM out-of-memory abort) never answers its in-flight requests:
      // fail them now so their callers fall back to a server scan instead of waiting forever.
      for (const [id, p] of pending) {
        if (p.entry !== entry) continue;
        pending.delete(id);
        entry.inflight = Math.max(0, entry.inflight - 1);
        p.reject(new Error('scan worker failed'));
      }
    };
    worker.postMessage(initMsg);
  });
}

// Create the whole pool. Rejects if any worker fails to init, so callers fall back
// to plain uploads. EP/threads are resolved once and shared across the pool.
function ensurePool(env) {
  if (poolPromise) return poolPromise;
  const coi = typeof globalThis !== 'undefined' && globalThis.crossOriginIsolated === true;
  const hc = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined;
  // Use the backend the capability probe SETTLED on (WebGPU only if it built AND met
  // the budget; else WASM). If the pool is somehow built before any probe ran, fall
  // back to the config attempt. Decided ONCE — the whole pool runs one backend.
  const useWebGpu = probeResult && probeResult.backend
    ? probeResult.backend === 'webgpu'
    : webGpuEnabled();
  let override = null;
  try { override = typeof localStorage !== 'undefined' ? localStorage.getItem('ph_scan_pool') : null; } catch { /* sandboxed */ }
  const size = scanPoolSize({ hardwareConcurrency: hc, crossOriginIsolated: coi, webgpu: useWebGpu, override });
  const wasmThreads = wasmThreadCount({ crossOriginIsolated: coi, hardwareConcurrency: hc, poolSize: size });
  const { faceModelUrl, personModelUrl, textDetModelUrl, textRecModelUrl, textDictUrl } = MODELS(env);
  // ocrUseWebGpu: main-thread-only trial flag (workers can't read localStorage), default
  // OFF. WASM stays the trusted OCR backend until the parity gate greens on WebGPU.
  const initMsg = { type: 'init', faceModelUrl, personModelUrl, useWebGpu, wasmThreads, ocrUseWebGpu: ocrWebGpuEnabled() };
  // Hand the worker the text-OCR models ONLY when the flag is on — otherwise the worker
  // never loads them (face-only behaviour, unchanged). Phase 1, OFF by default.
  if (textScanEnabled()) Object.assign(initMsg, { textDetModelUrl, textRecModelUrl, textDictUrl });
  // Surface the resolved pool/thread/backend config to the metrics HUD (staging-gated).
  noteScanConfig({ poolSize: size, wasmThreads, crossOriginIsolated: coi, backend: useWebGpu ? 'webgpu' : 'wasm' });
  // Log the chosen backend once so the decision is visible in the console.
  console.log(
    `[photohawk scan] on-device backend = ${useWebGpu ? 'WebGPU' : 'WASM'} · workers=${size}` +
    (useWebGpu
      ? ''
      : ` · wasmThreads=${wasmThreads}${coi ? ' (multi-threaded)' : ' (single-threaded — page not cross-origin isolated)'}`),
  );
  poolPromise = Promise.all(Array.from({ length: size }, () => spawnWorker(initMsg)));
  return poolPromise;
}

/**
 * Tear the pool down and free its memory. Terminates every scan worker (releasing
 * each one's onnxruntime WASM heap and any in-flight decoded bitmap) and rejects
 * outstanding requests so their awaiters unblock and fall back to a server scan.
 * The next scanFile() lazily rebuilds the pool. Call this when the upload queue
 * goes idle or is cancelled so a large batch doesn't leave hundreds of MB of scan
 * memory resident — the pressure that freezes the tab when cancelling a big run.
 */
export function shutdownScanPool() {
  const p = poolPromise;
  poolPromise = null;
  // Stop host-load sampling and reset the governor so the next batch starts fresh
  // (any waiters are woken by govReset so their scanFile() unblocks and falls back).
  try { stopScanSampling(); } catch { /* best-effort */ }
  govReset();
  // Unblock anything awaiting an in-flight scan; best-effort → they fall back.
  for (const [, waiter] of pending) {
    try { waiter.reject(new Error('scan pool shut down')); } catch { /* already settled */ }
  }
  pending.clear();
  const terminate = () => {
    for (const w of spawnedWorkers) { try { w.terminate(); } catch { /* already gone */ } }
    spawnedWorkers.clear();
  };
  // Wait for any in-flight spawn to settle before terminating, so a worker created
  // mid-shutdown is still torn down rather than leaked.
  if (p) p.then(terminate, terminate); else terminate();
}

/**
 * Probe whether on-device scanning is viable on THIS device (loads the face model
 * + times one warm-up inference within a budget, on the ACTIVE backend). Memoised.
 * @returns {Promise<{ ok: boolean, reason?: string, warmupMs?: number, backend?: string }>}
 */
export async function probe(env) {
  if (probeResult) return probeResult;
  try {
    const ort = await import('onnxruntime-web');
    const { faceModelUrl } = MODELS(env);
    probeResult = await probeScanSupport(ort, { faceModelUrl, useWebGpu: webGpuEnabled() });
  } catch (e) {
    probeResult = { ok: false, reason: String((e && e.message) || e) };
  }
  // Log the decision once — the outcome of "try WebGPU, else WASM, else off".
  if (probeResult.ok) {
    console.log(
      `[photohawk scan] capability probe: OK — backend=${probeResult.backend}, ` +
      `${Math.round(probeResult.warmupMs || 0)}ms/inference`,
    );
  } else {
    console.log(`[photohawk scan] capability probe: on-device scanning DISABLED — ${probeResult.reason}`);
  }
  return probeResult;
}

/**
 * Decode `file` and run face + person detection off-thread on the least-busy
 * pool worker. Decode happens inside the worker.
 * @returns {Promise<{ faces: Array, people: Array }>} each detection carries
 *          { confidence, box, cropBlob }.
 */
export async function scanFile(file, env, { wantPeople = true, wantFaces = true, wantText = false } = {}) {
  const t0 = now();
  const pool = await ensurePool(env);
  // Keep the host-load samplers live while scanning so the governor's hostPressure()
  // signal is real in every env (idempotent; torn down on pool shutdown).
  startScanSampling();
  // Adaptive backpressure: wait for an admission slot. Under host pressure the
  // governor admits fewer concurrent scans; when healthy it's full-width (no wait).
  const releaseSlot = await govAcquire();
  try {
    reqId += 1;
    const id = reqId;
    // Least-busy dispatch: keeps every worker fed without a queue of its own.
    const entry = pool.reduce((a, b) => (b.inflight < a.inflight ? b : a));
    entry.inflight += 1;
    const result = await new Promise((resolve, reject) => {
      // A scan that never answers (hung or crashed worker) must not hold its caller — and with it
      // the batch — forever: give up after SCAN_TIMEOUT_MS; the photo then gets a server scan.
      const timer = setTimeout(() => {
        if (!pending.has(id)) return;
        pending.delete(id);
        entry.inflight = Math.max(0, entry.inflight - 1);
        reject(new Error('scan timed out'));
      }, SCAN_TIMEOUT_MS);
      pending.set(id, {
        entry,
        resolve: (v) => { clearTimeout(timer); resolve(v); },
        reject: (e) => { clearTimeout(timer); reject(e); },
      });
      // Transfer the File to the worker (structured clone of a Blob is by-reference,
      // no byte copy) and let it decode — no main-thread createImageBitmap.
      // wantText only takes effect when the flag armed the worker with text models (init).
      entry.worker.postMessage({ type: 'detect', id, blob: file, wantPeople, wantFaces, wantText: wantText && textScanEnabled() });
    });
    const totalMs = now() - t0;
    recordScan(totalMs, result.decodeMs || 0, result.inferMs || 0);
    // Forward the worker's per-stage timing + EP payload into the observability
    // aggregator (staging-gated; a no-op in prod). Additive fields off the result
    // message — falls back gracefully for an older worker that didn't send them.
    const timing = result.timing || {};
    recordScanMetrics({
      decodeMs: result.decodeMs || 0,
      faceMs: timing.faceMs,
      personMs: timing.personMs,
      ocrMs: timing.ocrMs,
      ocrCrops: timing.ocrCrops,
      totalMs,
      eps: result.eps,
    });
    return result;
  } finally {
    // Always free the admission slot (success, worker error, or pool shutdown) so the
    // governor never wedges. Referenced so hostPressure stays an imported live signal.
    void hostPressure;
    releaseSlot();
  }
}

// ---- Scan-speed instrumentation ------------------------------------------
// So you can tell how fast on-device scanning actually is on a given device:
// per-file timing accumulates here and is readable via getScanStats(). Call
// window.__phScanStats() in the console for a live snapshot at any time.
const stats = { count: 0, totalMs: 0, decodeMs: 0, inferMs: 0, minMs: Infinity, maxMs: 0, lastMs: 0, startedAt: 0 };

function recordScan(totalMs, decodeMs, inferMs) {
  if (!stats.count) stats.startedAt = now();
  stats.count += 1;
  stats.totalMs += totalMs;
  stats.decodeMs += decodeMs;
  stats.inferMs += inferMs;
  stats.lastMs = totalMs;
  if (totalMs < stats.minMs) stats.minMs = totalMs;
  if (totalMs > stats.maxMs) stats.maxMs = totalMs;
}

/** Live scan-speed snapshot: per-photo average/min/max (ms) and throughput (photos/min). */
export function getScanStats() {
  const avgMs = stats.count ? stats.totalMs / stats.count : 0;
  // Throughput is wall-clock across the whole batch (pool overlaps work), so
  // photos/min is elapsed-based, not 1/avg — a pool finishes faster than avg implies.
  const elapsedMin = stats.startedAt ? (now() - stats.startedAt) / 60000 : 0;
  const perMin = elapsedMin > 0 ? Math.round(stats.count / elapsedMin) : 0;
  return {
    count: stats.count,
    avgMs: Math.round(avgMs),
    decodeAvgMs: stats.count ? Math.round(stats.decodeMs / stats.count) : 0,
    inferAvgMs: stats.count ? Math.round(stats.inferMs / stats.count) : 0,
    minMs: stats.count ? Math.round(stats.minMs) : 0,
    maxMs: Math.round(stats.maxMs),
    lastMs: Math.round(stats.lastMs),
    photosPerMin: perMin,
    poolSize: poolSize(),
    backend: webGpuEnabled() ? 'webgpu' : 'wasm',
  };
}

if (typeof window !== 'undefined') window.__phScanStats = getScanStats;

// Test/reset hook — drop the memoised probe + pool (e.g. between unit tests).
export function _reset() {
  probeResult = null;
  if (poolPromise) poolPromise.then((ws) => ws.forEach((e) => e.worker.terminate())).catch(() => {});
  poolPromise = null;
  pending.clear();
}
